import { event } from "../core/EventUtil.js";
import { merge } from "../core/Merge.js";
import { Signal } from "../core/Signal.js";

export interface IdleOptions {
    duration?: number; // Duration in seconds before idle state is triggered
    scope?: Document | HTMLElement;
    enabled?: boolean;
    preventIdle?: () => boolean;
}

export class IdleClass {
    public onIdle = Signal.create();
    public onReset = Signal.create();

    private _isIdle = true;
    private _duration: number; // Duration in seconds before idle state is triggered
    private _timeoutId: number | null = null;
    private _enabled: boolean = false;
    private _disposables: DisposableObject[] = [];
    private _scope: Document | HTMLElement;
    private _preventIdle: () => boolean = null;

    constructor(options: IdleOptions) {
        options = merge(
            { duration: 30, enabled: true, scope: document },
            options
        );
        this._preventIdle = options.preventIdle || null;
        this._duration = options.duration;
        this._scope = options.scope;
        this.enabled = options.enabled;
        this.reset();
        this._isIdle = true;
    }

    public reset(): void {
        if (!this._enabled) {
            return; // If idle detection is not enabled, do nothing
        }

        if (this._isIdle) {
            this.onReset.dispatch();
        }
        this._isIdle = false;

        if (this._timeoutId !== null) {
            clearTimeout(this._timeoutId);
        }
        this._timeoutId = window.setTimeout(() => {
            this._timeoutId = null;

            if (this._preventIdle && this._preventIdle()) {
                this.reset();
                return;
            }

            this.idle();
        }, this._duration * 1000);
    }

    public idle(): void {
        if (this._isIdle) {
            return; // Already idle, do nothing
        }
        if (this._timeoutId !== null) {
            clearTimeout(this._timeoutId);
        }
        this._isIdle = true;
        this.onIdle.dispatch();
    }

    public get isIdle(): boolean {
        return this._isIdle;
    }

    public get duration(): number {
        return this._duration;
    }

    public set duration(value: number) {
        this._duration = value;
        this.reset(); // Reset the timeout with the new duration
    }

    public get enabled(): boolean {
        return this._enabled;
    }

    public set enabled(value: boolean) {
        if (this._enabled === value) {
            return; // No change, do nothing
        }
        this._enabled = value;
        if (this._enabled) {
            this._disposables.push(
                event.on({
                    scope: this._scope,
                    type: [
                        "mousedown",
                        "mousemove",
                        "click",
                        "keydown",
                        "touchstart",
                        "focusin",
                        // "touchmove",
                        // "touchend",
                        "wheel",
                    ],
                    listener: () => this.reset(),
                    capture: true,
                    passive: true,
                })
            );
            this.reset();
        } else {
            this._isIdle = true;
            this._disposables.forEach((d) => d.dispose());
            this._disposables = [];
            if (this._timeoutId !== null) {
                clearTimeout(this._timeoutId);
                this._timeoutId = null;
            }
        }
    }

    public dispose(): void {
        this._disposables.forEach((d) => d.dispose());
        this._disposables = [];
        if (this._timeoutId !== null) {
            clearTimeout(this._timeoutId);
            this._timeoutId = null;
        }
        this.onIdle.dispose();
        this.onIdle = null;
    }
}

export const Idle = new IdleClass({
    duration: 30,
    scope: document,
    enabled: false,
});
